import { paintTopicPrimaryAction, paintDetail } from './topicDetailPresentation';
import type { ResumeResult, TopicDetail } from './topicDetailPresentationUtils';
import { paintTopicViewerState } from './topicDetailViewPresentation';
import type { PlanCode, PrototypeRuntime, Root } from './prototypeTypes';
import type { TopicRecord } from './topicStore';
import {
	beginTopicReturnReconciliation,
	canReconcileTopicReturn,
	invalidateTopicReturnReconciliation,
	isCurrentTopicReturnReconciliation,
	rememberTopicReturnPlan,
	rememberTopicReturnValue,
	topicReturnPlanChanged,
	topicReturnValueChanged,
} from './topicReturnSnapshot';
import { acknowledgeChapterProgress, baselineChapterProgress, latestChapterProgress } from './chapterProgressTracker';

/**
 * Keeps the Topic DOM visible on an in-session Chapter -> Topic return, then
 * revalidates independent evidence in the background. Failed requests leave
 * the existing, already-rendered content untouched.
 */
export async function reconcileTopicReturn(
	root: Root,
	topic: TopicRecord,
	plan: PlanCode,
	runtime: PrototypeRuntime,
	options: { forcePrimaryAction?: boolean } = {},
): Promise<void> {
	const generation = beginTopicReturnReconciliation(root, topic.slug, runtime);
	if (generation === null) return;
	const optimisticProgress = latestChapterProgress(runtime, topic.slug);
	const [detailResp, chaptersResp, keywordResp, resumeResult] = await Promise.all([
		runtime.api.topicDetail(topic.slug).catch(() => null),
		runtime.api.listTopicChapters(topic.slug).catch(() => null),
		runtime.api.topicKeywords(topic.slug).catch(() => null),
		plan === 'visitor'
			? Promise.resolve({ loaded: true, response: null })
			: runtime.api
					.getResumeCard(50)
					.then((response) => ({ loaded: true, response }))
					.catch(() => null),
	]);
	if (!isCurrentTopicReturnReconciliation(root, topic.slug, runtime, generation) || !isTopicViewActive(root)) return;

	const planChanged = topicReturnPlanChanged(root, plan);
	const detailRecord = detailResp?.topic;
	const detail = detailRecord as TopicDetail | undefined;
	if (detail && detailRecord) {
		const detailChanged = topicReturnValueChanged(root, 'detail', detailRecord);
		const keywords = keywordResp?.keywords;
		const keywordsChanged = keywords !== undefined && topicReturnValueChanged(root, 'keywords', keywords);
		if (detailChanged || keywordsChanged || planChanged || chaptersResp?.access?.ownerAccess) paintDetail(root, detail, plan, keywords, chaptersResp?.access);
		paintTopicViewerState(root, detail);
		rememberTopicReturnValue(root, 'detail', detailRecord);
		if (keywords !== undefined) rememberTopicReturnValue(root, 'keywords', keywords);
		if (resumeResult && (topicReturnValueChanged(root, 'resume', resumeResult.response) || planChanged || optimisticProgress || options.forcePrimaryAction)) {
			paintTopicPrimaryAction(root, detail, plan, resumeResult as ResumeResult, 1, optimisticProgress);
			rememberTopicReturnValue(root, 'resume', resumeResult.response);
			const confirmed = resumeResult.response?.recent?.find((item) => (item.topicSlug === topic.slug || item.topicId === detail.id) && item.chapterIndex === optimisticProgress?.chapterIndex);
			if (confirmed && optimisticProgress?.chapterId) acknowledgeChapterProgress(runtime, topic.slug, optimisticProgress.chapterId, optimisticProgress.chapterIndex);
		} else if (optimisticProgress) {
			paintTopicPrimaryAction(root, detail, plan, { loaded: false, response: null }, 1, optimisticProgress);
		}
	}

	if (chaptersResp?.chapters && (topicReturnValueChanged(root, 'chapters', chaptersResp.chapters) || planChanged || chaptersResp.access?.ownerAccess)) {
		const { paintTopicChapters } = await import('./topicController');
		if (isCurrentTopicReturnReconciliation(root, topic.slug, runtime, generation) && isTopicViewActive(root)) {
			paintTopicChapters(root, topic.slug, plan, runtime, chaptersResp.chapters, chaptersResp.access);
		}
	}
	rememberTopicReturnPlan(root, plan);
}

/**
 * A chapter request can fail after the user has already returned to Topic.
 * Invalidate the stale concurrent refresh, synchronously remove that exact
 * navigation bridge, then obtain fresh authoritative evidence in the back.
 */
export function recoverTopicReturnAfterProgressRollback(root: Root, topic: TopicRecord, plan: PlanCode, runtime: PrototypeRuntime): void {
	// The rejected request may belong to Topic A after the user has already
	// navigated to Topic B. Its tracker token can be cleared, but it must never
	// repaint or revalidate the currently visible Topic B DOM.
	if (runtime.activeCourseId !== topic.slug || !canReconcileTopicReturn(root, topic.slug, runtime)) return;
	invalidateTopicReturnReconciliation(root, topic.slug, runtime);
	if (!isTopicViewActive(root)) return;
	paintTopicPrimaryAction(root, topic, plan, { loaded: false, response: null }, 1, baselineChapterProgress(runtime, topic.slug));
	void reconcileTopicReturn(root, topic, plan, runtime, { forcePrimaryAction: true });
}

function isTopicViewActive(root: Root): boolean {
	return root.querySelector('#view-topic')?.classList.contains('active') ?? false;
}
